import { useEffect, useLayoutEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { Router } from "react-router-dom";
import { HostNavigationCommandSchema, navigationBridgeEvent } from "@froppio/host-auth";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { useRouteTelemetry } from "@/core/telemetry/routeTelemetry";
import { reportError } from "@/core/telemetry/telemetry";
import { clearNavigationSession, createAppHistory, type AppHistory } from "./appHistory";
import { NavigationContext } from "./NavigationContext";

function ManagedRouter({ history, children }: { history: AppHistory; children: ReactNode }) {
  const snapshot = useSyncExternalStore(history.subscribe, history.getSnapshot);
  useLayoutEffect(() => history.listen(), [history]);
  useRouteTelemetry(snapshot.location.pathname, snapshot.action);
  useEffect(() => {
    const send = () => {
      const { canGoBack, busy, error } = history.getSnapshot();
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        channel: navigationBridgeEvent, version: 1, type: "state", canGoBack, busy: busy || !!error,
      }));
    };
    const receive = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const command = HostNavigationCommandSchema.safeParse(event.detail);
      if (!command.success) {
        console.error("The native app sent an unsupported navigation command.");
        return;
      }
      if (command.data.type === "back") history.navigator.go(-1);
      send();
    };
    window.addEventListener(navigationBridgeEvent, receive);
    const unsubscribe = history.subscribe(send);
    send();
    return () => { unsubscribe(); window.removeEventListener(navigationBridgeEvent, receive); };
  }, [history]);
  if (snapshot.error) return <NavigationError error={snapshot.error} />;
  return (
    <NavigationContext.Provider value={history}>
      <Router location={snapshot.location} navigationType={snapshot.action} navigator={history.navigator} unstable_useTransitions={false}>
        {children}
      </Router>
    </NavigationContext.Provider>
  );
}

function NavigationError({ error }: { error: Error }) {
  const [retryError, setRetryError] = useState<Error>();
  useEffect(() => { reportError(error, { source: "navigation" }); }, [error]);
  const retry = () => {
    try { clearNavigationSession(); window.location.reload(); }
    catch (cause) { setRetryError(cause instanceof Error ? cause : new Error("Unable to restart navigation.")); }
  };
  return <div className="mx-auto flex min-h-dvh max-w-lg items-center p-4">
    <ErrorState title="Unable to navigate" message={(retryError ?? error).message} onRetry={retry} />
  </div>;
}

export function AppRouter({ children }: { children: ReactNode }) {
  const [result] = useState<{ history: AppHistory } | { error: Error }>(() => {
    try { return { history: createAppHistory(window) }; }
    catch (cause) { return { error: cause instanceof Error ? cause : new Error("Could not initialize app navigation.") }; }
  });
  return "history" in result ? <ManagedRouter history={result.history}>{children}</ManagedRouter> : <NavigationError error={result.error} />;
}
